"use client";

import { FEED_API } from "@/lib/apis";
import { IGetCommunityFeedPostsResponseDTO, TCommunityFeedPost } from "@/types/post";
import { useInfiniteQuery } from "@tanstack/react-query";
import { Copy, FileText, Loader2Icon, PlusSquare, Users } from "lucide-react";
import { useMemo } from "react";
import { useInView } from "react-intersection-observer";
import FeedPost from "./feed-post";
import FeedSkeletonLoading from "./feed-skeleton-loading";
import { useAuth } from "@/providers/auth-provider";
import { useCommunityContext } from "@/providers/community-provider";
import Link from "next/link";
import { Button } from "@/components/new/button";
import toast from "react-hot-toast";
import { useModal } from "@/providers/modal-provider";
import { parsePostContentJSON } from "@/lib/utils";
import PinnedPostsCarousel from "./pinned-posts-carousel";

interface IFeedProps {
	communitySlug?: string;
	channelSlug?: string;
	isPublicFeed?: boolean;
	isChannelModerator?: boolean;
	isCommunityModerator?: boolean;
	pinnedPosts?: boolean;
}

const fetchPosts = async ({
	communitySlug,
	channelSlug,
	pageParam = 1,
	perPageParam = 10,
	isPublicFeed,
	pinnedPosts,
}: {
	communitySlug?: string;
	channelSlug?: string;
	pageParam: number;
	perPageParam?: number;
	isPublicFeed?: boolean;
	pinnedPosts?: boolean;
}) => {
	const url = new URL(FEED_API);
	url.searchParams.set("page", pageParam.toString());
	url.searchParams.set("perPage", perPageParam.toString());

	if (communitySlug) {
		url.searchParams.set("community-slug", communitySlug);
	}

	if (channelSlug) {
		url.searchParams.set("channel-slug", channelSlug);
	}

	if (isPublicFeed) {
		url.searchParams.set("is-public-feed", "true");
	}

	if (pinnedPosts) {
		url.searchParams.set("pinned-posts", "true");
	}

	const response = await fetch(url);
	const data = await response.json();

	// If unauthorized, throw an error to be caught by React Query
	if (!response.ok) {
		throw new Error(data?.message || "You do not have access to view this feed.");
	}

	return data;
};

const WelcomeMessage = ({
	handleClick,
}: {
	user: any;
	handleClick: () => void;
	isChannelModerator: boolean;
	isCommunityModerator: boolean;
}) => (
	<div className="flex flex-col items-start justify-start gap-10 rounded-md bg-white p-8 shadow-sm max-md:flex-wrap">
		<div className="flex flex-col gap-4">
			<h2 className="text-3xl font-medium">Welcome Admin,</h2>
			<h3 className="text-2xl font-normal">Here are few things to do to get things started</h3>
		</div>
		<div className="flex flex-col gap-6">
			<div className="flex items-center gap-2">
				<FileText />
				Update details & settings of community in your admin dashboard
			</div>
			<div className="flex items-start gap-2">
				<Users />
				<div className="flex flex-col gap-2">
					Invite people via admin dashboard or using this link
					<div className="flex items-center gap-2">
						<Link
							href={window.location.href}
							className="text-blue-600 underline hover:text-blue-800"
						>
							{window.location.href.length > 30
								? `${window.location.href.substring(0, 30)}...`
								: window.location.href}
						</Link>
						<Button
							onClick={() => {
								navigator.clipboard.writeText(`${window.location.href}`);
								toast.dismiss();
								toast.success("Link copied to clipboard");
							}}
							className="ml-2 flex items-center bg-transparent p-1 hover:bg-transparent"
						>
							<Copy className="h-4 w-4 text-blue-600 hover:text-blue-800" />
						</Button>
					</div>
				</div>
			</div>

			<div className="flex items-start gap-4">
				<PlusSquare />
				<div className="flex flex-col gap-2">
					Create post, get started
					<Button className="w-[120px] " onClick={handleClick}>
						Create Post
					</Button>
				</div>
			</div>
		</div>
	</div>
);

const NoPostsMessage = () => <p className="py-6 text-center text-gray-500">No discussions found</p>;

const ErrorMessage = ({ message }: { message: string }) => (
	<p className="py-6 text-center text-gray-500">{message}</p>
);

const Feed = ({
	communitySlug,
	channelSlug,
	isPublicFeed = false,
	isChannelModerator = false,
	isCommunityModerator = false,
}: IFeedProps) => {
	const { user, isLoggedIn } = useAuth();
	const { communityInfo, channelInfo } = useCommunityContext();

	const { openAuthModal, openCreatePostModal } = useModal();

	const handleClick = () => {
		if (!isLoggedIn) {
			openAuthModal();
			return;
		}
		openCreatePostModal();
	};
	const { hasNextPage, fetchNextPage, isFetchingNextPage, isLoading, error, isError, data } =
		useInfiniteQuery<IGetCommunityFeedPostsResponseDTO>({
			enabled: !!communitySlug,
			queryKey: channelSlug ? ["posts", communitySlug, channelSlug] : ["posts", communitySlug],
			queryFn: ({ pageParam }) =>
				fetchPosts({
					communitySlug: communitySlug,
					channelSlug: channelSlug,
					pageParam,
					perPageParam: 10,
					isPublicFeed,
					pinnedPosts: !!communityInfo || !!channelInfo,
				}),
			getNextPageParam: (lastPage) =>
				lastPage.pagination?.page ? lastPage.pagination?.page + 1 : undefined,
			staleTime: 1000 * 60,
			refetchOnMount: true,
			retry: 2,
		});

	const { mergedPosts, mergedPinnedPosts } = useMemo(() => {
		const formattedPosts: TCommunityFeedPost[] = [];
		const formattedPinnedPosts: TCommunityFeedPost[] = [];

		data?.pages?.forEach((page) => {
			if (page?.data) {
				page?.data?.posts.forEach((post) => formattedPosts.push(parsePostContentJSON(post)!));
				page?.data?.pinnedPosts?.forEach((post) =>
					formattedPinnedPosts.push(parsePostContentJSON(post)!)
				);
			}
		});

		return { mergedPosts: formattedPosts, mergedPinnedPosts: formattedPinnedPosts };
	}, [data?.pages]);

	const { ref } = useInView({
		rootMargin: "0px 0px 1000px 0px",
		threshold: 0,
		onChange: (inView) => inView && hasNextPage && !isFetchingNextPage && fetchNextPage(),
	});

	const isAdminOrModerator = user?.isAdmin || isCommunityModerator || isChannelModerator;

	if (isLoading) {
		return <FeedSkeletonLoading />;
	}

	if (isError) {
		return <ErrorMessage message={(error as Error).message} />;
	}

	if (mergedPosts.length === 0) {
		return isAdminOrModerator ? (
			<WelcomeMessage
				user={user}
				handleClick={handleClick}
				isChannelModerator={isChannelModerator}
				isCommunityModerator={isCommunityModerator}
			/>
		) : (
			<NoPostsMessage />
		);
	}

	const TRIGGER_NEXTPAGE_AT_POST = -3; // 3rd last post.

	return (
		<section>
			<div className="space-y-4">
				{mergedPinnedPosts.length > 1 ? (
					<PinnedPostsCarousel posts={mergedPinnedPosts} />
				) : (
					mergedPinnedPosts.map((post) => <FeedPost key={post.id} post={post} />)
				)}
				{mergedPosts.map((post, i) => (
					<FeedPost
						key={post.id}
						post={post}
						ref={i === mergedPosts.length + TRIGGER_NEXTPAGE_AT_POST ? ref : undefined}
					/>
				))}
			</div>

			<div className="mt-6">
				{(hasNextPage || isFetchingNextPage) && (
					<div className="flex justify-center p-4">
						<Loader2Icon className="h-6 w-6 animate-spin text-brand-primary" />
					</div>
				)}
				{!hasNextPage && mergedPosts.length > 0 && (
					<div className="flex items-center justify-center gap-x-1 text-sm font-medium text-gray-400">
						<span>You have reached the end.</span>
						{/* TODO [Bug]: The button below is not working */}
						<button
							className="underline"
							onClick={() => window?.scrollTo({ top: 0, left: 0, behavior: "smooth" })}
						>
							Back to top
						</button>
					</div>
				)}
			</div>
		</section>
	);
};

export default Feed;
